import React, { useCallback } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import {
	getPartnerCompanyEvent,
	getPartnerSubcontractedEvents,
	getEventTypes,
	getPartnerCompany,
	getFeatureFlags,
	getTags,
} from 'src/api/partner';
import SubcontractingTab from 'src/components/partner-portal/SubcontractingTab';
import { Spinner, Alert } from '@repo/components';
import { getVerifyPermission, constructQuery } from '@repo/shared/helpers';
import { PERMISSIONS } from '@repo/shared/domain';
import { EventEmitter } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/partner_/$partnerId/events/$eventId/subcontracting')({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>) => ({
		page: (search.page as number) || 0,
		sort: (search.sort as string) || 'startTime:desc',
	}),
});

function RouteComponent() {
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const { partnerId, eventId } = Route.useParams();
	useBreadcrumbLevel(3, 'Subcontracting');
	const { page, sort } = Route.useSearch();
	const queryClient = useQueryClient();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	// Get parent event data
	const {
		data: parentEvent,
		isLoading: eventLoading,
		error: eventError,
	} = useQuery({
		queryKey: ['partner-company-event', partnerId, eventId],
		queryFn: () => getPartnerCompanyEvent(partnerId, eventId),
		enabled: !userLoading && !!authenticatedUser,
	});

	// Company-portal pattern: GET .../events/:eventId/subcontracting → listSubcontractedEventsForGroup (hierarchy under root)
	const { data: eventsResponse, isLoading: eventsLoading } = useQuery({
		queryKey: ['partner-company-event-subcontracting', partnerId, eventId, { page, sort }],
		queryFn: () => getPartnerSubcontractedEvents(partnerId, eventId, constructQuery({ page: page.toString(), sort })),
		enabled: !userLoading && !!authenticatedUser && !!eventId && !!parentEvent?.projectId,
	});

	// Get event types
	const { data: eventTypesResponse, isLoading: eventTypesLoading } = useQuery({
		queryKey: ['event-types'],
		queryFn: () => getEventTypes(),
		enabled: !userLoading && !!authenticatedUser,
	});

	const eventTypes = eventTypesResponse?.data || [];

	// Get partner company (subcontractingGroup)
	const { data: partnerCompany, isLoading: companyLoading } = useQuery({
		queryKey: ['partner-company', partnerId],
		queryFn: () => getPartnerCompany(partnerId),
		enabled: !userLoading && !!authenticatedUser,
	});

	// Get feature flags
	const { data: featureFlags } = useQuery({
		queryKey: ['feature-flags'],
		queryFn: () => getFeatureFlags(),
		enabled: !userLoading && !!authenticatedUser,
	});

	// Get tags
	const { data: tags } = useQuery({
		queryKey: ['partner-tags', partnerId],
		queryFn: () => getTags(),
		enabled: !userLoading && !!authenticatedUser,
	});

	const handleReload = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['partner-company-event-subcontracting', partnerId, eventId] });
		queryClient.invalidateQueries({ queryKey: ['partner-company-event', partnerId, eventId] });
	}, [queryClient, partnerId, eventId]);

	const handleAddEvent = useCallback(
		async (data: unknown) => {
			try {
				const response = await fetch(`/api/partner-portal/groups/${partnerId}/events/${eventId}/subcontracting`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(data),
				});
				if (!response.ok) throw new Error('Failed to create event');
				EventEmitter.emit('toast', { type: 'success', message: 'Event created successfully' });
				handleReload();
			} catch (_error) {
				EventEmitter.emit('toast', { type: 'error', message: 'Failed to create event' });
			}
		},
		[partnerId, eventId, handleReload],
	);

	if (userLoading || eventLoading || eventsLoading || eventTypesLoading || companyLoading) {
		return (
			<div className="flex items-center justify-center h-full">
				<Spinner className="text-[#1E1E1E]" />
			</div>
		);
	}

	if (eventError || !parentEvent) {
		return (
			<div className="p-6">
				<Alert variant="destructive">Event not found</Alert>
			</div>
		);
	}

	if (!parentEvent.projectId) {
		return (
			<div className="p-6">
				<Alert variant="warning">This event is not associated with a project.</Alert>
			</div>
		);
	}

	return (
		<div className="p-6">
			<SubcontractingTab
				authenticatedUser={authenticatedUser}
				events={eventsResponse}
				addEvent={handleAddEvent}
				onReload={handleReload}
				types={eventTypes}
				parentEventId={eventId}
				parentEvent={parentEvent}
				project={parentEvent.project}
				permissions={{
					view: verifyPermission(PERMISSIONS.partnerCompanyEventsView),
					create: verifyPermission(PERMISSIONS.partnerCompanyEventsCreate),
					update: verifyPermission(PERMISSIONS.partnerCompanyEventsEdit),
					archive: false,
				}}
				featureFlags={featureFlags}
				projectId={parentEvent.projectId}
				groupId={partnerId}
				partnerId={partnerId}
				disableNavigation={false}
				isCompanyPortalEvent={true}
				subcontractingGroup={partnerCompany}
				tags={tags || []}
				usesTags={featureFlags?.USES_TAGS || false}
			/>
		</div>
	);
}
